import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTrash, FaUndo, FaCheck, FaEdit, FaStop } from 'react-icons/fa';

import { useAlertDialog } from '../../contexts/AlertDialogContext';

const InvitationManager = ({ 
    countries, 
    cities, 
    sessions, 
    onCountryAdd, 
    onCityAdd, 
    onSessionAdd, 
    onCountryDelete, 
    onCityDelete, 
    onSessionDelete,
    onEndSession,
    onEditSession,
    onRelaunchSession // New prop
}) => {
    const { openAlertDialog } = useAlertDialog();
    const { i18n } = useTranslation();
    const [view, setView] = useState('sessions');
    const [sessionFilterType, setSessionFilterType] = useState('all');

    const [newCountry, setNewCountry] = useState({ name: { fr: '', en: '' } });
    const [newCity, setNewCity] = useState({ countryId: '', name: { fr: '', en: '' } });

    const sessionTypes = ["Session Adulte (26 ans et plus)", "Session Jeune (18 à 25 ans)", "Session Libre (tout âge)"];
    const [newSession, setNewSession] = useState({
        cityId: '',
        title: sessionTypes[0],
        language: 'Français',
        address: '',
        date: '',
        startTime: '',
        endTime: '',
    });
    const [showAddSessionForm, setShowAddSessionForm] = useState(false);

    const handleAddCountry = (e) => {
        e.preventDefault();
        if (!newCountry.name.fr || !newCountry.name.en) return openAlertDialog('Les noms français et anglais sont requis.', 'Champs manquants');
        onCountryAdd(newCountry);
        setNewCountry({ name: { fr: '', en: '' } });
    };

    const handleAddCity = (e) => {
        e.preventDefault();
        if (!newCity.countryId || !newCity.name.fr || !newCity.name.en) return openAlertDialog('Tous les champs sont requis.', 'Champs manquants');
        onCityAdd(newCity);
        setNewCity({ countryId: '', name: { fr: '', en: '' } });
    };

    const handleAddSession = (e) => {
        e.preventDefault();
        if (!newSession.cityId || !newSession.title || !newSession.address || !newSession.date || !newSession.startTime || !newSession.endTime) {
            return openAlertDialog('Tous les champs de la session sont requis.', 'Champs manquants');
        }
        const startDateTime = new Date(`${newSession.date}T${newSession.startTime}:00`);
        const endDateTime = new Date(`${newSession.date}T${newSession.endTime}:00`);
        const transformedSession = {
            ...newSession,
            start_time: startDateTime.toISOString(),
            end_time: endDateTime.toISOString(),
        };
        delete transformedSession.date;
        delete transformedSession.startTime;
        delete transformedSession.endTime;
        onSessionAdd(transformedSession);
        setNewSession({ cityId: '', title: sessionTypes[0], language: 'Français', address: '', date: '', startTime: '', endTime: '' });
        setShowAddSessionForm(false);
    };

    const handleCancelAddSession = () => {
        setNewSession({ cityId: '', title: sessionTypes[0], language: 'Français', address: '', date: '', startTime: '', endTime: '' });
        setShowAddSessionForm(false);
    };

    const renderLocations = () => (
        <div className="link-section">
            <h2 style={{ marginTop: 0 }}>Gérer les Lieux (Pays et Villes)</h2>
            <div className="location-management-section">
                <div className="management-form">
                    <h3>Ajouter un Pays</h3>
                    <form onSubmit={handleAddCountry} className="link-form">
                        <input name="fr" value={newCountry.name.fr} onChange={(e) => setNewCountry(p => ({...p, name: {...p.name, fr: e.target.value}}))} placeholder="Nom du pays (FR)" required />
                        <input name="en" value={newCountry.name.en} onChange={(e) => setNewCountry(p => ({...p, name: {...p.name, en: e.target.value}}))} placeholder="Nom du pays (EN)" required />
                        <button type="submit">Ajouter Pays</button>
                    </form>
                </div>
                <div className="management-form">
                    <h3>Ajouter une Ville</h3>
                    <form onSubmit={handleAddCity} className="link-form">
                        <select name="countryId" value={newCity.countryId} onChange={(e) => setNewCity(p => ({...p, countryId: e.target.value}))} required>
                            <option value="" disabled>Sélectionner un pays</option>
                            {countries.map(c => <option key={c.id} value={c.id}>{c.name[i18n.language.split('-')[0]]}</option>)}                        </select>
                        <input name="fr" value={newCity.name.fr} onChange={(e) => setNewCity(p => ({...p, name: {...p.name, fr: e.target.value}}))} placeholder="Nom de la ville (FR)" required />
                        <input name="en" value={newCity.name.en} onChange={(e) => setNewCity(p => ({...p, name: {...p.name, en: e.target.value}}))} placeholder="Nom de la ville (EN)" required />
                        <button type="submit">Ajouter Ville</button>
                    </form>
                </div>
            </div>
            <div className="location-tables-section">
                <div className="location-table">
                    <h3>Liste des Pays</h3>
                    <table>
                        <thead><tr><th>Nom ({i18n.language})</th><th>Actions</th></tr></thead>
                        <tbody>
                            {countries.map(c => <tr key={c.id}><td>{c.name[i18n.language.split('-')[0]]}</td><td><button onClick={() => onCountryDelete(c.id)} className="action-btn btn-delete"><FaTrash /></button></td></tr>)}
                        </tbody>
                    </table>
                </div>
                <div className="location-table">
                    <h3>Liste des Villes</h3>
                    <table>
                        <thead><tr><th>Ville ({i18n.language})</th><th>Pays</th><th>Actions</th></tr></thead>
                        <tbody>
                            {cities.map(city => {
                                const country = countries.find(c => c.id === city.countryId);
                                return (
                                    <tr key={city.id}>
                                        <td>{city.name[i18n.language.split('-')[0]]}</td>
                                        <td>{country ? country.name[i18n.language.split('-')[0]] : 'N/A'}</td>
                                        <td><button onClick={() => onCityDelete(city.id)} className="action-btn btn-delete"><FaTrash /></button></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    const renderSessions = () => {
        const now = new Date();
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const tomorrowStart = new Date(todayStart);
        tomorrowStart.setDate(tomorrowStart.getDate() + 1);

        const getFilteredSessions = (allSessions) => {
            switch (sessionFilterType) {
                case 'ongoing':
                    return allSessions.filter(s => {
                        const sessionStartDate = new Date(s.startTime);
                        return sessionStartDate >= todayStart && sessionStartDate < tomorrowStart && !s.is_terminated;
                    });
                case 'future':
                    return allSessions.filter(s => new Date(s.startTime) >= tomorrowStart && !s.is_terminated);
                case 'terminated':
                    return allSessions.filter(s => s.is_terminated);
                case 'all':
                default:
                    // Show sessions that are either active (today or future) OR terminated
                    return allSessions.filter(s => (new Date(s.startTime) >= todayStart && !s.is_terminated) || s.is_terminated);
            }
        };

        // Calculate counts for filter buttons
        const allSessionsCount = sessions.filter(s => (new Date(s.startTime) >= todayStart && !s.is_terminated) || s.is_terminated).length;
        const ongoingCount = sessions.filter(s => {
            const sessionStartDate = new Date(s.startTime);
            return sessionStartDate >= todayStart && sessionStartDate < tomorrowStart && !s.is_terminated;
        }).length;
        const futureCount = sessions.filter(s => new Date(s.startTime) >= tomorrowStart && !s.is_terminated).length;
        const terminatedCount = sessions.filter(s => s.is_terminated).length;

        const displayedSessions = getFilteredSessions(sessions);

        return (
            <div className="link-section">
                <h2 style={{ marginTop: 0 }}>Gérer les Sessions</h2>
                <nav className="tertiary-nav" style={{ marginBottom: "20px" }}>
                    <button
                        onClick={() => setSessionFilterType("all")}
                        className={sessionFilterType === "all" ? "active" : ""}
                    >
                        Toutes ({allSessionsCount})
                    </button>
                    <button
                        onClick={() => setSessionFilterType("ongoing")}
                        className={
                            sessionFilterType === "ongoing" ? "active" : ""
                        }
                    >
                        Du jour ({ongoingCount})
                    </button>
                    <button
                        onClick={() => setSessionFilterType("future")}
                        className={
                            sessionFilterType === "future" ? "active" : ""
                        }
                    >
                        Futures ({futureCount})
                    </button>
                    <button
                        onClick={() => setSessionFilterType("terminated")}
                        className={
                            sessionFilterType === "terminated" ? "active" : ""
                        }
                    >
                        Stop ({terminatedCount})
                    </button>
                </nav>
                <button
                    onClick={() => setShowAddSessionForm(true)}
                    className="action-btn btn-add-session"
                    style={{ marginBottom: "1rem" }}
                >
                    + Ajouter une nouvelle session
                </button>

                {showAddSessionForm && (
                    <div
                        className="management-form"
                        style={{ marginBottom: "2rem" }}
                    >
                        <h3>Ajouter une Session</h3>
                        <form
                            onSubmit={handleAddSession}
                            className="link-form session-form"
                        >
                            <select
                                name="cityId"
                                value={newSession.cityId}
                                onChange={(e) =>
                                    setNewSession((p) => ({
                                        ...p,
                                        cityId: e.target.value,
                                    }))
                                }
                                required
                            >
                                <option value="" disabled>
                                    Sélectionner une ville
                                </option>
                                {cities.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.name[i18n.language.split("-")[0]]}
                                    </option>
                                ))}{" "}
                            </select>
                            <select
                                name="title"
                                value={newSession.title}
                                onChange={(e) =>
                                    setNewSession((p) => ({
                                        ...p,
                                        title: e.target.value,
                                    }))
                                }
                                required
                            >
                                {sessionTypes.map((type) => (
                                    <option key={type} value={type}>
                                        {type}
                                    </option>
                                ))}
                            </select>
                            <input
                                type="text"
                                name="address"
                                value={newSession.address}
                                onChange={(e) =>
                                    setNewSession((p) => ({
                                        ...p,
                                        address: e.target.value,
                                    }))
                                }
                                placeholder="Adresse du lieu"
                                required
                            />
                            <select
                                name="language"
                                value={newSession.language}
                                onChange={(e) =>
                                    setNewSession((p) => ({
                                        ...p,
                                        language: e.target.value,
                                    }))
                                }
                            >
                                <option value="Français">Français</option>
                                <option value="Anglais">Anglais</option>
                                <option value="Bilingue">Bilingue</option>
                            </select>
                            <div className='group-forme'>
                                <div className="form-group">
                                    <label>Date:</label>
                                    <input
                                        type="date"
                                        name="date"
                                        value={newSession.date}
                                        onChange={(e) =>
                                            setNewSession((p) => ({
                                                ...p,
                                                date: e.target.value,
                                            }))
                                        }
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Heure de Début:</label>
                                    <input
                                        type="time"
                                        name="startTime"
                                        value={newSession.startTime}
                                        onChange={(e) =>
                                            setNewSession((p) => ({
                                                ...p,
                                                startTime: e.target.value,
                                            }))
                                        }
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Heure de Fin:</label>
                                    <input
                                        type="time"
                                        name="endTime"
                                        value={newSession.endTime}
                                        onChange={(e) =>
                                            setNewSession((p) => ({
                                                ...p,
                                                endTime: e.target.value,
                                            }))
                                        }
                                        required
                                    />
                                </div>
                            </div>

                            <div className="modal-actions">
                                {" "}
                                {/* Using modal-actions for consistent button styling */}
                                <button type="submit">Ajouter Session</button>
                                <button
                                    type="button"
                                    onClick={handleCancelAddSession}
                                    className="action-btn btn-cancel"
                                >
                                    Annuler
                                </button>
                            </div>
                        </form>
                    </div>
                )}
                <div className="location-table">
                    <div style={{ marginBottom: "10px", fontSize: "0.9em" }}>
                        <span
                            style={{
                                backgroundColor: "#d4edda",
                                padding: "3px 8px",
                                borderRadius: "4px",
                                marginRight: "10px",
                            }}
                        >
                            Du jour (Vert)
                        </span>
                        <span
                            style={{
                                backgroundColor: "#ffeeba",
                                padding: "3px 8px",
                                borderRadius: "4px",
                                marginRight: "10px",
                            }}
                        >
                            Future (Orange)
                        </span>
                        <span
                            style={{
                                backgroundColor: "#f8d7da",
                                padding: "3px 8px",
                                borderRadius: "4px",
                            }}
                        >
                            Stop (Rouge)
                        </span>
                    </div>
                    <h3>Liste des Sessions</h3>
                    <table>
                        <thead>
                            <tr>
                                <th>Titre</th>
                                <th>Ville</th>
                                <th>Adresse</th>
                                <th>Langue</th>
                                <th>Date</th>
                                <th>Heure de Début</th>
                                <th>Heure de Fin</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayedSessions.map((s) => {
                                const city = cities.find(
                                    (c) => c.id === s.cityId
                                );
                                const rowClass = s.is_terminated
                                    ? "session-terminated-red"
                                    : (() => {
                                          const sessionStartDate = new Date(
                                              s.startTime
                                          );
                                          if (
                                              sessionStartDate >= todayStart &&
                                              sessionStartDate < tomorrowStart
                                          )
                                              return "session-ongoing-green";
                                          if (sessionStartDate >= tomorrowStart)
                                              return "session-future-orange";
                                          return "";
                                      })();

                                return (
                                    <tr key={s.id} className={rowClass}>
                                        <td>{s.title}</td>
                                        <td>
                                            {city
                                                ? city.name[
                                                      i18n.language.split(
                                                          "-"
                                                      )[0]
                                                  ]
                                                : "N/A"}
                                        </td>
                                        <td>{s.address}</td>
                                        <td>{s.language}</td>
                                        <td>
                                            {new Date(
                                                s.startTime
                                            ).toLocaleDateString(i18n.language)}
                                        </td>
                                        <td>
                                            {new Date(
                                                s.startTime
                                            ).toLocaleTimeString(i18n.language)}
                                        </td>
                                        <td>
                                            {new Date(
                                                s.endTime
                                            ).toLocaleTimeString(i18n.language)}
                                        </td>
                                        <td className="action-cell">
                                            {s.is_terminated ? (
                                                <button
                                                    onClick={() =>
                                                        onRelaunchSession(s)
                                                    }
                                                    className="action-btn btn-relaunch"
                                                    title="Relancer la session"
                                                >
                                                    <FaUndo /> Relancer
                                                </button>
                                            ) : (
                                                <>
                                                    <button
                                                        onClick={() =>
                                                            onEndSession(s)
                                                        }
                                                        className="action-btn btn-end-session"
                                                        title="Stopper la session"
                                                    >
                                                        <FaStop /> Stop
                                                    </button>
                                                    <button
                                                        onClick={() =>
                                                            onEditSession(s)
                                                        }
                                                        className="action-btn btn-edit"
                                                        title="Modifier la session"
                                                    >
                                                        <FaEdit /> Modifier
                                                    </button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    return (
        <div className="invitation-manager">
            <nav className="tertiary-nav">
                <button onClick={() => setView('locations')} className={view === 'locations' ? 'active' : ''}>Gérer les Lieux</button>
                <button onClick={() => setView('sessions')} className={view === 'sessions' ? 'active' : ''}>Gérer les Sessions</button>
            </nav>
            <div className="management-content">
                {view === 'locations' ? renderLocations() : renderSessions()}
            </div>
        </div>
    );
};

export default InvitationManager;
